import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { fmtEmpty } from '../format.js';

export interface KeyValueItem {
  key: string;
  value: ReactNode;
  /** Render the value in mono (IDs, timestamps, hashes). */
  mono?: boolean;
  /** Tint the value. */
  tone?: 'ok' | 'warn' | 'info' | 'brand' | 'mute';
  /** Rows layout: let a long value (a sentence, a link) wrap onto further lines instead of truncating. */
  wrap?: boolean;
}

export interface KeyValueProps {
  items: KeyValueItem[];
  /** `rows` (label left, value right, per line) or `grid` (label over value, 2 columns). */
  layout?: 'rows' | 'grid';
  /** Number of grid columns when layout is grid. Default 2. */
  columns?: 2 | 3 | 4;
  className?: string;
}

const TONE: Record<NonNullable<KeyValueItem['tone']>, string> = {
  ok: 'text-ok',
  warn: 'text-warn',
  info: 'text-info',
  brand: 'text-brand-text',
  mute: 'text-mute',
};

/**
 * Grid columns step down to two when the container is too narrow for the mono labels (about 85px
 * each plus the 24px gutter), so a label never runs into the neighbouring cell in a narrow card or
 * at high zoom. Container queries rather than viewport breakpoints: the same grid sits in cards of
 * very different widths.
 */
const GRID_COLUMNS: Record<NonNullable<KeyValueProps['columns']>, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 @min-[19rem]:grid-cols-3',
  4: 'grid-cols-2 @min-[26rem]:grid-cols-4',
};

/** Definition list with mono labels. Empty values render `--`. */
export function KeyValue({ items, layout = 'rows', columns = 2, className }: KeyValueProps) {
  if (layout === 'grid') {
    return (
      <div className={cn('@container min-w-0', className)}>
        <dl className={cn('grid gap-x-6 gap-y-4', GRID_COLUMNS[columns])}>
          {items.map((item) => (
            <div key={item.key} className="min-w-0">
              <dt className="label-mono [overflow-wrap:anywhere]">{item.key}</dt>
              <dd className={cn('mt-1 min-w-0 text-nav text-ink [overflow-wrap:anywhere]', item.mono ? 'font-mono text-xs' : '', item.tone ? TONE[item.tone] : '')}>
                {renderValue(item.value)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }
  return (
    <dl className={cn('flex min-w-0 flex-col', className)}>
      {items.map((item) => (
        <div key={item.key} className="flex items-baseline justify-between gap-4 border-b border-hair py-2 last:border-b-0">
          <dt className="label-mono shrink-0">{item.key}</dt>
          <dd className={cn('min-w-0 text-right text-table text-ink', item.wrap ? '[overflow-wrap:anywhere]' : 'truncate', item.mono ? 'font-mono text-xs' : '', item.tone ? TONE[item.tone] : '')}>
            {renderValue(item.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function renderValue(value: ReactNode): ReactNode {
  if (value === null || value === undefined || value === '') return <span className="font-mono text-mute">{fmtEmpty(null)}</span>;
  return value;
}
